import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { CheckCircle, Clock, Utensils, Wine, LayoutList, Layers, RefreshCw } from 'lucide-react';
import { EDOrder, EDTable, EDVendorType } from '../../types/EventDay';
import { describeEventDayError, eventDayApi, getSocketAuth, type EventDayProblem } from '../../services/EventDayApiClient';
import { useNotifications } from '../NotificationProvider';

interface KitchenBarViewProps {
  vendor: EDVendorType;
  onOrderCountChange?: (count: number) => void;
  onRefreshRef?: (fn: () => void) => void;
  eventId?: string | null;
  viewMode?: 'kanban' | 'batch';
  onViewModeChange?: (m: 'kanban' | 'batch') => void;
}

const VIP_KEYWORDS = ['vip', 'vvip', 'bride', 'groom', 'family', 'head'];
const DONE_WINDOW_MS = 2 * 60 * 60 * 1000; // show served orders for 2h

function minutesAgo(ts: string) {
  return Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
}

function slaColor(ts: string): { border: string; bg: string; dot: string } {
  const m = minutesAgo(ts);
  if (m < 5)  return { border: '#10b981', bg: 'rgba(16,185,129,0.06)',  dot: '#10b981' };
  if (m < 15) return { border: '#f59e0b', bg: 'rgba(245,158,11,0.06)',  dot: '#f59e0b' };
  return       { border: '#ef4444', bg: 'rgba(239,68,68,0.06)',  dot: '#ef4444' };
}

function slaLabel(ts: string) {
  const m = minutesAgo(ts);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function tableLabel(order: EDOrder, tables: EDTable[]): { num: string; tag?: string } {
  const t = tables.find(x => x.id === order.table_id);
  const num = `T${order.table_number ?? t?.number ?? order.table_id}`;
  const text = `${t?.name ?? ''} ${t?.section ?? ''}`.toLowerCase();
  const isVip = VIP_KEYWORDS.some(kw => text.includes(kw));
  const tag = isVip ? (t?.name ?? t?.section ?? 'VIP') : undefined;
  return { num, tag };
}

// ── Kanban card ──────────────────────────────────────────────────────
interface KanbanCardProps {
  order: EDOrder;
  tables: EDTable[];
  onAdvance?: () => void;
  done?: boolean;
}

function KanbanCard({ order, tables, onAdvance, done }: KanbanCardProps) {
  const { num, tag } = tableLabel(order, tables);
  const { border, bg } = done ? { border: '#334155', bg: 'transparent' } : slaColor(order.created_at);
  const mins = minutesAgo(order.created_at);
  const dotColor = slaColor(order.created_at).dot;

  return (
    <div className="kk-card" style={{ /* ds-dynamic: SLA-based color state */ borderColor: border, background: bg, opacity: done ? 0.6 : 1 }}>
      <div className="kk-card-header">
        <span className="kk-table-num">{num}</span>
        {tag && <span className="kk-vip-tag">{tag.toUpperCase()}</span>}
        <span className="kk-sla" style={{ /* ds-dynamic: SLA color from order age */ color: dotColor }}>
          <span className="kk-sla-dot" style={{ /* ds-dynamic: SLA color from order age */ background: dotColor }} />
          {slaLabel(order.created_at)}
        </span>
      </div>

      <div className="kk-items">
        {(order.items || []).map((item, i) => (
          <div key={i} className="kk-item-line">
            <span className="kk-item-qty">{item.quantity}×</span>
            <span className="kk-item-name">{item.name}</span>
          </div>
        ))}
      </div>

      {order.notes && <p className="kk-notes">{order.notes}</p>}

      {onAdvance && !done && (
        <button className="kk-action-btn" onClick={onAdvance}
          style={{ /* ds-dynamic: SLA-based color state */ borderColor: border, color: border }}>
          {order.status === 'pending' ? 'Start Preparing' : <><CheckCircle size={13} /> Mark Served</>}
        </button>
      )}
    </div>
  );
}

// ── Kanban column ─────────────────────────────────────────────────────
interface KanbanColProps {
  title: string;
  count: number;
  accent: string;
  children: React.ReactNode;
}

function KanbanCol({ title, count, accent, children }: KanbanColProps) {
  return (
    <div className="kk-col">
      <div className="kk-col-header" style={{ /* ds-dynamic: column accent color from props */ borderTopColor: accent }}>
        <span className="kk-col-title">{title}</span>
        {count > 0 && <span className="kk-col-count" style={{ /* ds-dynamic: column accent color from props */ background: accent }}>{count}</span>}
      </div>
      <div className="kk-col-body">
        {children}
      </div>
    </div>
  );
}

// ── Batch row ─────────────────────────────────────────────────────────
interface BatchGroup {
  itemName: string;
  totalQty: number;
  tableNums: string[];
  oldestTs: string;
}

function BatchRow({ group }: { group: BatchGroup }) {
  const dotColor = slaColor(group.oldestTs).dot;
  return (
    <div className="kk-batch-row">
      <div className="kk-batch-header">
        <span className="kk-batch-name">{group.itemName}</span>
        <span className="kk-batch-qty">{group.totalQty} portions</span>
        <span className="kk-sla-dot kk-sla-dot--ml-auto" style={{ /* ds-dynamic: SLA color from order age */ background: dotColor }} />
        <span className="kk-batch-age" style={{ /* ds-dynamic: SLA color from order age */ color: dotColor }}>{slaLabel(group.oldestTs)}</span>
      </div>
      <div className="kk-batch-tables">
        {group.tableNums.map(t => (
          <span key={t} className="kk-batch-table-chip">{t}</span>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────
const KitchenBarView: React.FC<KitchenBarViewProps> = ({ vendor, onOrderCountChange, onRefreshRef, eventId, viewMode: externalViewMode, onViewModeChange }) => {
  const [allOrders, setAllOrders] = useState<EDOrder[]>([]);
  const [tables, setTables] = useState<EDTable[]>([]);
  const [flashing, setFlashing] = useState(false);
  const [internalViewMode, setInternalViewMode] = useState<'kanban' | 'batch'>('kanban');
  const viewMode = externalViewMode ?? internalViewMode;
  const setViewMode = (m: 'kanban' | 'batch') => { setInternalViewMode(m); onViewModeChange?.(m); };
  const [socket, setSocket] = useState<Socket | null>(null);
  const prevCountRef = useRef(0);
  const { showError } = useNotifications();
  // First load shows a spinner, a failure with nothing to show is an error box, and a failure after data keeps the board with a notice.
  const [loadState, setLoadState] = useState<'loading' | 'ready'>('loading');
  const [problem, setProblem] = useState<EventDayProblem | null>(null);

  useEffect(() => {
    eventDayApi.getTables(eventId ?? undefined)
      .then(d => setTables(d.tables || []))
      .catch(() => {});
  }, []);

  const loadOrders = async () => {
    try {
      const d = await eventDayApi.getOrders();
      const vendorOrders = ((d.orders || []) as EDOrder[]).filter(o => o.vendor === vendor);
      const active = vendorOrders.filter(o => ['pending', 'preparing'].includes(o.status));
      if (active.length > prevCountRef.current) {
        setFlashing(true);
        setTimeout(() => setFlashing(false), 1500);
      }
      prevCountRef.current = active.length;
      setAllOrders(vendorOrders);
      onOrderCountChange?.(active.length);
      setProblem(null);
    } catch (err) {
      setProblem(describeEventDayError(err));
    } finally {
      setLoadState('ready');
    }
  };

  useEffect(() => { onRefreshRef?.(loadOrders); }, [vendor]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 15000);
    const s = io(eventDayApi.getBaseUrl(), { auth: getSocketAuth() });
    s.on('order:created', (o: EDOrder) => { if (o.vendor === vendor) loadOrders(); });
    s.on('order:updated', () => loadOrders());
    setSocket(s);
    return () => { clearInterval(interval); s.disconnect(); };
  }, [vendor]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (orderId: string, newStatus: string) => {
    try {
      await eventDayApi.updateOrder(orderId, newStatus);
      await loadOrders();
    } catch (err) { showError(describeEventDayError(err).message); }
  };

  // Kanban columns
  const pending   = allOrders.filter(o => o.status === 'pending')
                              .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const preparing = allOrders.filter(o => o.status === 'preparing')
                              .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const served    = allOrders
    .filter(o => (o.status as string) === 'complete' || (o.status as string) === 'served')
    .filter(o => Date.now() - new Date(o.created_at).getTime() < DONE_WINDOW_MS)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 15);

  // Batch view: group pending+preparing by dish name
  const batchOrders = [...pending, ...preparing];
  const batchMap = new Map<string, BatchGroup>();
  for (const order of batchOrders) {
    const tLabel = tableLabel(order, tables).num;
    for (const item of order.items || []) {
      const existing = batchMap.get(item.name);
      if (existing) {
        existing.totalQty += item.quantity;
        if (!existing.tableNums.includes(tLabel)) existing.tableNums.push(tLabel);
        if (new Date(order.created_at) < new Date(existing.oldestTs)) existing.oldestTs = order.created_at;
      } else {
        batchMap.set(item.name, { itemName: item.name, totalQty: item.quantity, tableNums: [tLabel], oldestTs: order.created_at });
      }
    }
  }
  const batchGroups = Array.from(batchMap.values()).sort((a, b) => b.totalQty - a.totalQty);

  const vendorLabel = vendor === 'food' ? 'Kitchen' : 'Bar';
  const VendorIcon = vendor === 'food' ? Utensils : Wine;

  if (loadState === 'loading') {
    return (
      <div className="kk-screen">
        <div className="ed-loading" role="status"><RefreshCw size={24} className="ed-spin" aria-hidden /><p>Loading {vendorLabel.toLowerCase()} orders…</p></div>
      </div>
    );
  }

  if (problem && allOrders.length === 0) {
    return (
      <div className="kk-screen">
        <div className="ed-error-state" role="alert">
          <h3>{problem.kind === 'notSetUp' ? 'Orders are not set up yet' : `${vendorLabel} orders could not load`}</h3>
          <p>{problem.message}</p>
          <button className="ed-btn-secondary" onClick={loadOrders}>Try again</button>
        </div>
      </div>
    );
  }

  if (pending.length === 0 && preparing.length === 0 && served.length === 0) {
    return (
      <div className="kk-screen">
        <div className="ed-vendor-clear">
          <CheckCircle size={48} className="ed-vendor-clear-icon" />
          <h3>{vendorLabel} is Clear</h3>
          <p>No active orders at this time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`kk-screen ${flashing ? 'ed-flashing' : ''}`}>
      {problem && <p className="ed-notice" role="status">Showing the last orders received. {problem.message}</p>}

      {viewMode === 'kanban' ? (
        <div className="kk-board">
          <KanbanCol title="Pending" count={pending.length} accent="#f59e0b">
            {pending.length === 0
              ? <div className="kk-col-empty">No pending orders</div>
              : pending.map(o => (
                <KanbanCard key={o.id} order={o} tables={tables}
                  onAdvance={() => updateStatus(o.id, 'preparing')} />
              ))
            }
          </KanbanCol>

          <KanbanCol title="Preparing" count={preparing.length} accent="#6366f1">
            {preparing.length === 0
              ? <div className="kk-col-empty">Nothing in progress</div>
              : preparing.map(o => (
                <KanbanCard key={o.id} order={o} tables={tables}
                  onAdvance={() => updateStatus(o.id, 'served')} />
              ))
            }
          </KanbanCol>

          <KanbanCol title="Served" count={0} accent="#10b981">
            {served.length === 0
              ? <div className="kk-col-empty">Nothing served yet</div>
              : served.map(o => (
                <KanbanCard key={o.id} order={o} tables={tables} done />
              ))
            }
          </KanbanCol>
        </div>
      ) : (
        <div className="kk-batch-board">
          <div className="kk-batch-header-row">
            <span className="kk-batch-col-title">Dish</span>
            <span className="kk-batch-col-sub">{batchGroups.length} items · {batchOrders.length} orders</span>
          </div>
          {batchGroups.length === 0
            ? <div className="kk-col-empty">No active orders to batch</div>
            : batchGroups.map(g => <BatchRow key={g.itemName} group={g} />)
          }
        </div>
      )}

      {socket && null}
    </div>
  );
};

export default KitchenBarView;
